import React from 'react';
import PropTypes from 'prop-types';
import COLORS from 'constants/colors';
import styled from 'styled-components';
import { Separator, Text } from '@cuemath/comps';

import { store } from 'redux-rest-reducer';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get as getDemoClassroom } from 'actions/demo-classroom';
import { get as getConfig } from 'actions/configs';
import { COUNTRIES_MAP } from 'constants/country-codes';

import StudentInfo from './student-info';
import TeacherFeedback from './teacher-feedback';
import ParentFeedback from './parent-feedback';
import DemoDetails from './demo-details';
import PointsToDiscuss from './points-to-discuss';

const INTEL_DEMO_CONFIG = 'intel_demo_config';
const PARENT_FEEDBACK = 'parent_feedback';
const TEACHER_DEMO_FEEDBACK = 'teacher_demo_feedback';
const SALES_PAGE_PITCH_LINKS = 'sales_page_pitch_links';

const PRODUCT_TYPE = {
  CODING: 'CODING',
};

const DEMO_TYPE = {
  MATH: 'Math',
  CODING: 'Coding',
};

const Wrapper = styled.div`
  background: ${COLORS.CLOUD};
  padding: 24px 12px;
`;

const Container = styled.div`
  max-width: 960px;
  margin: auto;
  background: ${COLORS.WHITE};
  border-radius: 12px;
  padding: 24px 32px;
`;

const StyledSeparator = styled(Separator)`
  margin: 16px 0px;
`;

class DemoClass extends React.Component {
  componentDidMount() {
    const {
      actions,
      intelConfig,
      parentConfig,
      salesConfig,
      studentClassroomId,
      teacherConfig,
    } = this.props;

    if (!studentClassroomId) return;

    if (!intelConfig) {
      actions.getConfig(INTEL_DEMO_CONFIG);
    }

    if (!parentConfig) {
      actions.getConfig(PARENT_FEEDBACK);
    }

    if (!teacherConfig) {
      actions.getConfig(TEACHER_DEMO_FEEDBACK);
    }

    if (!salesConfig) {
      actions.getConfig(SALES_PAGE_PITCH_LINKS);
    }

    actions.getDemoClassroom(studentClassroomId);
  }

  render() {
    const {
      demoClassroom,
      intelConfig,
      parentConfig,
      salesConfig,
      studentClassroomId,
      teacherConfig,
    } = this.props;
    const { data: demoData, __ERROR__: error, isLoading } = demoClassroom || {};
    const {
      student,
      teacher,
      parent_demo_preferences: parentDemoPreferences,
      student_classroom: studentClassroom,
      user_attempts_analysis: attemptAnalysis,
      product_code: productCode,
      teacher_id: teacherId,
      student_id: studentId,
      sales_pitch: salesPitch,
    } = demoData || {};
    const { name, country_code: countryCode, grade } = student || {};
    const {
      parent_feedback: parentFeedback,
      teacher_classroom_feedback: teacherClassroomFeedback,
      teacher_feedback: teacherFeedback,
    } = studentClassroom || {};

    const demoType =
      productCode === PRODUCT_TYPE.CODING ? DEMO_TYPE.CODING : DEMO_TYPE.MATH;
    const { message } = error || {};
    const filteredCountries =
      Object.keys(COUNTRIES_MAP).filter(key => COUNTRIES_MAP[key].code === countryCode) ||
      [];
    const region = filteredCountries.map(id => COUNTRIES_MAP[id].name).join(' / ');

    if (
      !studentClassroomId ||
      !intelConfig ||
      !demoData ||
      !parentConfig ||
      !salesConfig ||
      !teacherConfig ||
      isLoading ||
      error
    ) {
      return (
        <Container>
          <Text>{message || 'Loading...'}</Text>
        </Container>
      );
    }

    return (
      <Wrapper>
        <Container>
          <StudentInfo
            demoType={demoType}
            region={region}
            student={student}
            studentClassroom={studentClassroom}
            teacher={teacher}
            teacherId={teacherId}
            parentDemoPreferences={parentDemoPreferences}
            studentId={studentId}
          />
          <StyledSeparator />
          <TeacherFeedback config={teacherConfig} feedback={teacherClassroomFeedback} />
          <StyledSeparator />
          <ParentFeedback feedback={parentFeedback} config={parentConfig} />
          <StyledSeparator />
          <DemoDetails
            attemptAnalysis={attemptAnalysis}
            config={intelConfig}
            stdName={name}
            studentClassroom={studentClassroom}
            studentClassroomId={studentClassroomId}
            teacherFeedback={teacherFeedback}
            productCode={productCode}
          />
          <StyledSeparator />
          <PointsToDiscuss
            attemptAnalysis={attemptAnalysis}
            demoType={demoType}
            grade={grade}
            salesPitch={salesPitch}
            salesConfig={salesConfig}
            teacherFeedback={teacherFeedback}
          />
        </Container>
      </Wrapper>
    );
  }
}

DemoClass.propTypes = {
  actions: PropTypes.shape({
    getConfig: PropTypes.func.isRequired,
    getDemoClassroom: PropTypes.func.isRequired,
  }).isRequired,
  demoClassroom: PropTypes.shape({}),
  intelConfig: PropTypes.shape({}),
  parentConfig: PropTypes.shape({}),
  salesConfig: PropTypes.shape({}),
  studentClassroomId: PropTypes.string,
  teacherConfig: PropTypes.shape({}),
};

DemoClass.defaultProps = {
  demoClassroom: null,
  intelConfig: null,
  parentConfig: null,
  salesConfig: null,
  studentClassroomId: null,
  teacherConfig: null,
};

export default connect(
  ({ demoClassroom, configs }, { match }) => {
    const {
      params: { studentClassroomId },
    } = match;
    const { data: intelData } = store.get(configs, INTEL_DEMO_CONFIG) || {};
    const { data: parentData } = store.get(configs, PARENT_FEEDBACK) || {};
    const { data: teacherData } = store.get(configs, TEACHER_DEMO_FEEDBACK) || {};
    const { data: salesData } = store.get(configs, SALES_PAGE_PITCH_LINKS) || {};

    return {
      demoClassroom: store.get(demoClassroom, studentClassroomId),
      intelConfig: intelData,
      parentConfig: parentData,
      salesConfig: salesData,
      studentClassroomId,
      teacherConfig: teacherData,
    };
  },
  dispatch => ({
    actions: bindActionCreators({ getDemoClassroom, getConfig }, dispatch),
  }),
)(DemoClass);
